import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Receipt, FileText, ChartLine, CurrencyInr,
    ArrowsClockwise, DownloadSimple, TrendUp, TrendDown, Minus,
    ArrowRight, Warning, Timer,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import AdminBilling from "@/pages/admin/Billing";
import { PatientInvoicesTab } from "@/components/admin/finance/PatientInvoicesTab";
import { FinanceReportsTab } from "@/components/admin/finance/FinanceReportsTab";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";


/* ============================================================================
   AdminFinance — `/admin/finance`
   ----------------------------------------------------------------------------
   Money-movement command center. Owns B2B invoicing (Invisique → Provider),
   patient-side billing log (Provider → Patient), and combined reports.

   iter101.32 — Pricing / Tiers / Promotions / Wizard Cards / Wizard Copy
   moved OUT to /admin/service-setup.
   iter101.32b — Shipped 6 mockup-review improvements:
      1) Controls bar with date-range picker (7/30/90/YTD/all)
      2) MoM trend chips (↑ 12% vs prev) via previous-period comparison
      3) Section-level quick actions (Create Invoice / View all)
      4) Patient-side "Paid on time %" clinical-health KPI
      5) Urgent tile styling (rose left-bar) for overdue counts
      6) Empty state with a real action
   ========================================================================== */

const TABS = [
    { id: "overview",        label: "Overview",        Icon: ChartLine,   hint: "Combined revenue posture" },
    { id: "b2b-billing",     label: "B2B Billing",     Icon: Receipt,     hint: "Invisique™ → Provider" },
    { id: "patient-billing", label: "Patient Billing", Icon: CurrencyInr, hint: "Provider → Patient" },
    { id: "reports",         label: "Reports",         Icon: FileText,    hint: "AR aging + revenue trend" },
];

const DAYS_OPTIONS = [
    { id: "7",   label: "Last 7 days" },
    { id: "30",  label: "Last 30 days" },
    { id: "90",  label: "Last 90 days" },
    { id: "ytd", label: "Year to date" },
    { id: "all", label: "All time" },
];

export default function AdminFinance() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
    const [tab, setTab] = useState(initialTab);
    const [days, setDays] = useState(() => localStorage.getItem("finance:days") || "30");
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);

    useEffect(() => {
        const urlTab = searchParams.get("tab");
        if (tab !== urlTab) {
            if (tab === "overview") searchParams.delete("tab");
            else searchParams.set("tab", tab);
            setSearchParams(searchParams, { replace: true });
        }
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        localStorage.setItem("finance:days", days);
    }, [days]);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/finance/overview?days=${encodeURIComponent(days)}`)
            .then((r) => { setOverview(r.data); setLastFetchedAt(new Date()); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [days, refreshKey]);

    return (
        <div className="space-y-6" data-testid="admin-finance-page">
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Operations · Finance</div>
                <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Finance Command Center</h1>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                    Revenue at a glance — B2B billing, patient-side logs, and AR reports.
                </p>
            </div>

            <ControlsBar
                days={days}
                onDaysChange={setDays}
                lastFetchedAt={lastFetchedAt}
                onRefresh={() => setRefreshKey((k) => k + 1)}
                loading={loading}
            />

            {overview && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="finance-kpi-strip">
                    <StatTile
                        label="B2B outstanding"
                        value={formatINR(overview.b2b?.outstanding)}
                        Icon={Receipt}
                        tone="rose"
                        trend={computeTrend(overview.b2b?.outstanding, overview.b2b_prev?.outstanding, "invert")}
                        testid="finance-kpi-b2b-outstanding"
                    />
                    <StatTile
                        label="B2B collected"
                        value={formatINR(overview.b2b?.collected)}
                        Icon={CurrencyInr}
                        tone="emerald"
                        trend={computeTrend(overview.b2b?.collected, overview.b2b_prev?.collected)}
                        testid="finance-kpi-b2b-collected"
                    />
                    <StatTile
                        label="Patient outstanding"
                        value={formatINR(overview.patient?.outstanding)}
                        Icon={CurrencyInr}
                        tone="amber"
                        trend={computeTrend(overview.patient?.outstanding, overview.patient_prev?.outstanding, "invert")}
                        testid="finance-kpi-patient-outstanding"
                    />
                </div>
            )}

            <AdminSegmentedTabs
                tabs={TABS}
                active={tab}
                onChange={setTab}
                testid="finance-tabs"
                tabTestidBase="finance-tab"
            />

            <div>
                {tab === "overview"        && <OverviewPanel overview={overview} onGoto={setTab} />}
                {tab === "b2b-billing"     && <AdminBilling embedded />}
                {tab === "patient-billing" && <PatientInvoicesTab />}
                {tab === "reports"         && <FinanceReportsTab />}
            </div>
        </div>
    );
}

/* --------------------------------------------------------------------------
   Controls bar — date-range picker + last-updated + refresh + export
   -------------------------------------------------------------------------- */
function ControlsBar({ days, onDaysChange, lastFetchedAt, onRefresh, loading }) {
    const relative = useMemo(() => (lastFetchedAt ? formatRelative(lastFetchedAt) : "—"), [lastFetchedAt]);
    const doExport = () => {
        toast.message("Export coming soon", { description: "CSV export of the current period's Finance KPIs will ship in the next update." });
    };
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-card border border-border rounded-2xl px-4 py-3" data-testid="finance-controls-bar">
            <div className="flex items-center gap-2">
                <label htmlFor="finance-days" className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Period</label>
                <select
                    id="finance-days"
                    value={days}
                    onChange={(e) => onDaysChange(e.target.value)}
                    className="h-9 rounded-md border border-input bg-card px-3 text-xs font-medium"
                    data-testid="finance-days-select"
                >
                    {DAYS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[11px] text-muted-foreground tabular-nums" data-testid="finance-last-updated">
                    Updated {relative}
                </span>
                <Button variant="outline" size="sm" className="rounded-full" onClick={onRefresh} disabled={loading} data-testid="finance-refresh-btn">
                    <ArrowsClockwise size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} weight="bold" />
                    Refresh
                </Button>
                <Button size="sm" className="rounded-full" onClick={doExport} data-testid="finance-export-btn">
                    <DownloadSimple size={13} className="mr-1.5" weight="bold" />
                    Export
                </Button>
            </div>
        </div>
    );
}

/* --------------------------------------------------------------------------
   Overview panel — B2B + Patient sections with quick actions in headers.
   -------------------------------------------------------------------------- */
function OverviewPanel({ overview, onGoto }) {
    if (!overview) {
        return <div className="text-muted-foreground text-sm py-10 text-center">Loading…</div>;
    }
    return (
        <div className="space-y-6" data-testid="finance-overview">
            <SectionHeader
                overline="B2B (Invisique™ → Provider)"
                title="Billing Overview"
                actions={(
                    <>
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => onGoto("b2b-billing")} data-testid="finance-ov-b2b-view">
                            View all invoices <ArrowRight size={12} className="ml-1.5" weight="bold" />
                        </Button>
                    </>
                )}
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile
                    label="Outstanding"
                    value={formatINR(overview.b2b?.outstanding)}
                    Icon={Receipt}
                    tone="rose"
                    trend={computeTrend(overview.b2b?.outstanding, overview.b2b_prev?.outstanding, "invert")}
                    testid="finance-ov-b2b-outstanding"
                />
                <StatTile
                    label="Collected"
                    value={formatINR(overview.b2b?.collected)}
                    Icon={CurrencyInr}
                    tone="emerald"
                    trend={computeTrend(overview.b2b?.collected, overview.b2b_prev?.collected)}
                    testid="finance-ov-b2b-collected"
                />
                <StatTile
                    label="Issued (total)"
                    value={formatINR(overview.b2b?.issued)}
                    Icon={ChartLine}
                    tone="primary"
                    trend={computeTrend(overview.b2b?.issued, overview.b2b_prev?.issued)}
                    testid="finance-ov-b2b-issued"
                />
                <StatTile
                    label="Overdue"
                    value={overview.b2b?.overdue_count ?? 0}
                    Icon={Warning}
                    tone="rose"
                    urgent={(overview.b2b?.overdue_count ?? 0) > 0}
                    testid="finance-ov-b2b-overdue"
                />
            </div>

            <SectionHeader
                overline="Patient (Provider → Patient)"
                title="Billing Overview"
                actions={(
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => onGoto("patient-billing")} data-testid="finance-ov-patient-view">
                        View all invoices <ArrowRight size={12} className="ml-1.5" weight="bold" />
                    </Button>
                )}
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile
                    label="Outstanding"
                    value={formatINR(overview.patient?.outstanding)}
                    Icon={Receipt}
                    tone="rose"
                    trend={computeTrend(overview.patient?.outstanding, overview.patient_prev?.outstanding, "invert")}
                    testid="finance-ov-patient-outstanding"
                />
                <StatTile
                    label="Collected"
                    value={formatINR(overview.patient?.collected)}
                    Icon={CurrencyInr}
                    tone="emerald"
                    trend={computeTrend(overview.patient?.collected, overview.patient_prev?.collected)}
                    testid="finance-ov-patient-collected"
                />
                <StatTile
                    label="Issued (total)"
                    value={formatINR(overview.patient?.issued)}
                    Icon={ChartLine}
                    tone="primary"
                    trend={computeTrend(overview.patient?.issued, overview.patient_prev?.issued)}
                    testid="finance-ov-patient-issued"
                />
                <StatTile
                    label="Paid on time"
                    value={overview.patient?.paid_on_time_pct == null ? "—" : `${overview.patient.paid_on_time_pct}%`}
                    Icon={Timer}
                    tone={overview.patient?.paid_on_time_pct != null && overview.patient.paid_on_time_pct < 60 ? "amber" : "emerald"}
                    testid="finance-ov-patient-on-time"
                />
            </div>

            {/* Analytics / Cash flow — canonical empty state with an actionable CTA */}
            <SectionHeader overline="Analytics" title="Cash-flow trends" />
            <EmptyStateWithAction
                icon={ChartLine}
                title="No data available for the selected period."
                subtitle="Pick a broader range or hop into Reports for AR aging + revenue trend charts."
                ctaLabel="Open Reports"
                onCta={() => onGoto("reports")}
                testid="finance-cashflow-empty"
            />
        </div>
    );
}

function SectionHeader({ overline, title, actions }) {
    return (
        <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">{overline}</div>
                <div className="font-display text-base font-bold tracking-tight mt-0.5">{title}</div>
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
    );
}

/* --------------------------------------------------------------------------
   Canonical StatTile — enhanced with:
     • `trend` prop → shows a small MoM chip (up/down/flat)
     • `urgent` prop → rose left border + rose bg tint for genuinely bad numbers
   -------------------------------------------------------------------------- */
export function StatTile({ label, value, Icon, tone = "primary", trend, urgent, testid }) {
    const accent = tone === "emerald" ? "bg-emerald-100 text-emerald-700"
        : tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : tone === "slate" ? "bg-slate-100 text-slate-700"
        : tone === "violet" ? "bg-violet-100 text-violet-700"
        : "bg-primary/10 text-primary";
    return (
        <div
            className={`flex items-start gap-3 rounded-xl p-3 ${urgent
                ? "bg-rose-50 border border-rose-200 border-l-4 border-l-rose-500"
                : "bg-card border border-border"}`}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className={`text-xl font-display font-bold tabular-nums leading-none mt-1 truncate ${urgent ? "text-rose-800" : ""}`}>{value}</div>
                {trend && <TrendChip {...trend} />}
            </div>
        </div>
    );
}

function TrendChip({ direction, label }) {
    const styles = direction === "up"
        ? "bg-emerald-50 text-emerald-700"
        : direction === "down"
            ? "bg-rose-50 text-rose-700"
            : "bg-secondary text-muted-foreground";
    const Icon = direction === "up" ? TrendUp : direction === "down" ? TrendDown : Minus;
    return (
        <div className={`inline-flex items-center gap-1 rounded-full ${styles} px-1.5 py-0.5 text-[10px] font-semibold tabular-nums mt-1.5`}>
            <Icon size={9} weight="bold" />
            <span>{label}</span>
        </div>
    );
}

/* Compute a `{direction, label}` object for the trend chip.
   `mode="invert"` flips green/red so that "outstanding went down" reads green
   (that's a good thing for owed money) and going up reads red. */
function computeTrend(current, previous, mode) {
    if (current == null || previous == null) return null;
    if (previous === 0 && current === 0) return { direction: "flat", label: "no change" };
    if (previous === 0) return { direction: mode === "invert" ? "down" : "up", label: "vs prev period" };
    const pct = ((current - previous) / previous) * 100;
    const rounded = Math.round(pct * 10) / 10;
    if (Math.abs(rounded) < 1) return { direction: "flat", label: "no change" };
    const isUp = pct > 0;
    const direction = mode === "invert" ? (isUp ? "down" : "up") : (isUp ? "up" : "down");
    const label = `${isUp ? "↑" : "↓"} ${Math.abs(rounded)}% vs prev`;
    return { direction, label };
}

function EmptyStateWithAction({ icon: Icon, title, subtitle, ctaLabel, onCta, testid }) {
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid={testid}>
            <div className="w-12 h-12 rounded-2xl bg-primary/10 grid place-items-center mx-auto mb-3">
                <Icon size={22} weight="duotone" className="text-primary" />
            </div>
            <h3 className="font-display text-sm font-bold">{title}</h3>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">{subtitle}</p>
            {ctaLabel && onCta && (
                <Button size="sm" className="rounded-full mt-4" onClick={onCta} data-testid={`${testid}-cta`}>
                    {ctaLabel} <ArrowRight size={12} weight="bold" className="ml-1.5" />
                </Button>
            )}
        </div>
    );
}

const formatINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function formatRelative(date) {
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}
